import React, { useEffect, useState } from 'react';
import {
    XCircle,
    CheckCircle,
    Calendar,
    Clock,
    Phone,
    User,
    Hash,
    MessageSquare,
    Send,
    AlertCircle,
    RefreshCw,
    PlusCircle,
    Loader2
} from 'lucide-react';
import { apiFetch } from '../api';
import { useWebSocket } from '../websocket/WebSocketContext';
import { ensureSocketConnection } from '../websocket/socketUtils';

export default function SMSDetails({ selectedSms, onClose, setError, loginId, deliveryMode }) {
    const { client, connected } = useWebSocket();
    const [showErrorPayload, setShowErrorPayload] = useState(false);
    const [smsBodyResponse, setSmsBodyResponse] = useState(null);
    const [isBodyLoading, setIsBodyLoading] = useState(false);
    const [isResending, setIsResending] = useState(false);
    const [actionSuccess, setActionSuccess] = useState('');
    const [actionError, setActionError] = useState('');

    const safeJson = async (response) => {
        try {
            return await response.json();
        } catch (e) {
            return null;
        }
    };

    const fetchSMSBodyById = async (smsId) => {
        if (!smsId) {
            setError('Missing sms id');
            return;
        }

        setIsBodyLoading(true);
        setError('');

        try {
            if (deliveryMode === 'socket') {
                // --- WEBSOCKET LOGIC ---
                try {
                    await ensureSocketConnection(client);
                } catch (connError) {
                    console.error(connError);
                    setError('Socket connection failed.');
                    setIsBodyLoading(false);
                    return;
                }

                // Assuming backend topic follows the pattern: /topic/getSmsBodyById/{loginId}
                const topic = `/topic/get/sms/body/response/${loginId}`;

                const subscription = client.subscribe(topic, (msg) => {
                    try {
                        const payload = JSON.parse(msg.body);
                        const data = (typeof payload === 'string') ? JSON.parse(payload) : payload;

                        if (data && data.STATUS === 'SUCCESS') {
                            setSmsBodyResponse(data.DATA);
                        } else {
                            setSmsBodyResponse(null);
                            setError('SMS body not found in socket response.');
                        }
                    } catch (e) {
                        console.error('Failed to parse SMS body socket response', e);
                        setError('Failed to parse SMS body data.');
                    } finally {
                        subscription.unsubscribe();
                        setIsBodyLoading(false);
                    }
                });

                // Publish request with smsId string
                client.publish({
                    destination: '/app/get/sms/body/response',
                    body: smsId
                });

            } else {
                // --- API LOGIC ---
                const url = `/get/sms/body/response?smsId=${encodeURIComponent(smsId)}`;
                const res = await apiFetch(url);
                if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);

                const data = await safeJson(res);
                if (data && data.STATUS === 'SUCCESS') {
                    setSmsBodyResponse(data.DATA);
                } else {
                    setSmsBodyResponse(null);
                    setError('SMS body not found in response.');
                }
                setIsBodyLoading(false);
            }
        } catch (err) {
            if (err.name === 'AbortError') return;
            console.error('Error fetching SMS body:', err);
            setError('Failed to load SMS body. Network error or API issue.');
            setSmsBodyResponse(null);
            setIsBodyLoading(false);
        }
    };

    const handleResendSMS = async () => {
        setIsResending(true);
        setActionSuccess('');
        setActionError('');

        // 1. Construct Payload (Shared Logic)
        let payload;
        try {
            if (selectedSms.smsVendor === 'PAYTM') {
                const bulkSmsArr = smsBodyResponse?.bulkSms?.map((bulk) => ({
                    mobileNo: bulk?.smsData?.receiver || bulk?.mobileNo || bulk?.receiver,
                    smsTemplate: bulk?.smsData?.content || bulk?.smsTemplate || bulk?.content,
                    appName: selectedSms.appName,
                    templateId: bulk?.smsData?.template_id || bulk?.template_id || '',
                    smsData: bulk?.smsData || null
                })) || [];

                payload = {
                    _id: selectedSms._id,
                    appName: selectedSms.appName,
                    smsVendor: 'PAYTM',
                    sms_bulk_type: selectedSms.sms_bulk_type,
                    bulkSms: bulkSmsArr
                };
            } else {
                if (selectedSms.sms_bulk_type) {
                    const bulkSmsArr = smsBodyResponse?.bulkSms?.map((bulk) => ({
                        mobileNo: bulk.mobileNo,
                        smsTemplate: bulk.smsTemplate,
                        appName: bulk.appName || selectedSms.appName,
                        smsData: bulk.smsData || null
                    })) || [];

                    payload = {
                        _id: selectedSms._id,
                        smsVendor: selectedSms.smsVendor,
                        sms_bulk_type: true,
                        appName: selectedSms.appName,
                        bulkSms: bulkSmsArr
                    };
                } else {
                    payload = {
                        _id: selectedSms._id,
                        smsVendor: selectedSms.smsVendor,
                        sms_bulk_type: false,
                        appName: selectedSms.appName,
                        sms: {
                            mobileNo: smsBodyResponse?.sms?.mobileNo,
                            smsTemplate: smsBodyResponse?.sms?.smsTemplate,
                            appName: smsBodyResponse?.sms?.appName || selectedSms.appName,
                            smsData: smsBodyResponse?.sms?.smsData || null
                        }
                    };
                }
            }
        } catch (prepError) {
            console.error("Error preparing resend payload:", prepError);
            setActionError("Failed to prepare SMS data for resending.");
            setIsResending(false);
            return;
        }

        // 2. Send Request (Socket vs API)
        try {
            if (deliveryMode === 'socket') {
                // --- WEBSOCKET LOGIC ---
                try {
                    await ensureSocketConnection(client);
                } catch (connError) {
                    console.error(connError);
                    setActionError('Socket connection failed.');
                    setIsResending(false);
                    return;
                }

                // Reuse the sendSms topic since resend uses the same endpoint logic usually
                const topic = `/topic/sendSms/${loginId}`;

                const subscription = client.subscribe(topic, (msg) => {
                    try {
                        const data = JSON.parse(msg.body);

                        if (data.STATUS === 'SUCCESS') {
                            setActionSuccess('SMS resent successfully! (Socket)');
                            setTimeout(() => setActionSuccess(''), 3000);
                        } else {
                            setActionError(`Failed to resend: ${data.MESSAGE || 'Unknown error'}`);
                            setTimeout(() => setActionError(''), 3000);
                        }
                    } catch (e) {
                        console.error('Failed to parse resend socket response', e);
                        setActionError('Failed to parse server response.');
                    } finally {
                        subscription.unsubscribe();
                        setIsResending(false);
                    }
                });

                client.publish({
                    destination: '/app/sendSms',
                    body: JSON.stringify(payload)
                });

            } else {
                // --- API LOGIC ---
                const response = await apiFetch('/sendSms', {
                    method: 'POST',
                    body: JSON.stringify(payload)
                });

                const data = await response.json();

                if (data.STATUS === 'SUCCESS') {
                    setActionSuccess('SMS resent successfully!');
                    setTimeout(() => setActionSuccess(''), 3000);
                } else {
                    setActionError(`Failed to resend: ${data.MESSAGE || 'Unknown error'}`);
                    setTimeout(() => setActionError(''), 3000);
                }
                setIsResending(false);
            }
        } catch (err) {
            console.error('Error resending SMS:', err);
            setActionError('Network or server error while resending.');
            setTimeout(() => setActionError(''), 3000);
            setIsResending(false);
        }
    };

    useEffect(() => {
        if (!selectedSms) return;
        fetchSMSBodyById(selectedSms._id);
    }, [selectedSms]);

    if (!selectedSms) return null;

    const getStatusColor = (response, status) => {
        if (status === 'SUCCESS' || response?.toLowerCase().includes('successfully') || response?.toLowerCase().includes('sent successfully')) {
            return 'text-green-400';
        }
        if (status === 'FAILED' || response?.toLowerCase().includes('error') || response?.toLowerCase().includes('invalid')) {
            return 'text-red-400';
        }
        return 'text-yellow-400';
    };

    const getStatusIcon = (response, status) => {
        if (status === 'SUCCESS' || response?.toLowerCase().includes('successfully') || response?.toLowerCase().includes('sent successfully')) {
            return CheckCircle;
        }
        if (status === 'FAILED' || response?.toLowerCase().includes('error') || response?.toLowerCase().includes('invalid')) {
            return XCircle;
        }
        return AlertCircle;
    };

    const formatPhoneNumber = (phone) => {
        if (!phone) return '';
        if (phone.length === 10) {
            return phone.replace(/(\d{5})(\d{5})/, '$1 $2');
        }
        return phone;
    };

    const normalizeBulkSmsResponse = (arr) => {
        if (!Array.isArray(arr)) return [];
        if (arr.length > 0 && Array.isArray(arr[0])) {
            return arr.flat().filter(Boolean);
        }
        return arr.filter(Boolean);
    };

    const extractMobileNumbers = (xmlString) => {
        if (!xmlString) return [];
        const regex = /TO="(\d+)"/g;
        let match;
        const numbers = [];
        while ((match = regex.exec(xmlString)) !== null) {
            numbers.push(match[1]);
        }
        return numbers;
    };

    const showResendButton = selectedSms.STATUS === 'FAILED';
    const showQueueButton = selectedSms.STATUS === 'PENDING';

    return (
        <>
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                <div className="bg-slate-900 border border-white/20 rounded-2xl p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                    <div className="flex items-center justify-between mb-6">
                        <h3 className="text-2xl font-bold text-white">SMS Details</h3>
                        <div className="flex items-center gap-3">
                            {smsBodyResponse?.payload_error_list && smsBodyResponse.payload_error_list.length > 0 && (
                                <button
                                    onClick={() => setShowErrorPayload(true)}
                                    className="px-4 py-2 bg-red-500/20 text-red-200 rounded-lg hover:bg-red-500/30 transition-all duration-200 border border-red-500/30 flex items-center gap-2"
                                >
                                    <AlertCircle className="w-4 h-4" />
                                    Error Payload
                                </button>
                            )}
                            <button
                                onClick={onClose}
                                className="w-8 h-8 bg-white/10 rounded-full flex items-center justify-center hover:bg-white/20 transition-all duration-200"
                            >
                                <XCircle className="w-5 h-5 text-white" />
                            </button>
                        </div>
                    </div>

                    {/* Action Success/Error Messages */}
                    {(actionSuccess || actionError) && (
                        <div
                            className={`mb-4 p-4 rounded-xl flex items-center space-x-2 ${actionSuccess
                                ? 'bg-green-500/20 border border-green-500/50 text-green-200'
                                : 'bg-red-500/20 border border-red-500/50 text-red-200'
                                }`}
                        >
                            {actionSuccess ? <CheckCircle className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
                            <span>{actionSuccess || actionError}</span>
                        </div>
                    )}

                    {/* Action Buttons */}
                    {(showResendButton || showQueueButton) && (
                        <div className="mb-6 flex gap-3">
                            {showResendButton && (
                                <button
                                    onClick={handleResendSMS}
                                    disabled={isResending}
                                    className="flex-1 px-4 py-3 bg-gradient-to-r from-red-500 to-orange-500 text-white rounded-xl font-semibold hover:from-red-600 hover:to-orange-600 transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {isResending ? (
                                        <>
                                            <Loader2 className="w-5 h-5 animate-spin" />
                                            <span>Resending...</span>
                                        </>
                                    ) : (
                                        <>
                                            <RefreshCw className="w-5 h-5" />
                                            <span>Resend SMS</span>
                                        </>
                                    )}
                                </button>
                            )}
                            {showQueueButton && (
                                <button
                                    onClick={handleResendSMS}
                                    disabled={isResending}
                                    className="flex-1 px-4 py-3 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-xl font-semibold hover:from-blue-600 hover:to-purple-600 transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {isResending ? (
                                        <>
                                            <Loader2 className="w-5 h-5 animate-spin" />
                                            <span>Adding to Queue...</span>
                                        </>
                                    ) : (
                                        <>
                                            <PlusCircle className="w-5 h-5" />
                                            <span>Add to Queue</span>
                                        </>
                                    )}
                                </button>
                            )}
                        </div>
                    )}

                    <div className="space-y-6">
                        {/* Basic Info */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {selectedSms?.rf1_id && (
                                <div>
                                    <p className="text-white/40 text-sm uppercase tracking-wide mb-1">Site Id</p>
                                    <p className="text-white text-sm font-mono break-all">{selectedSms.rf1_id}</p>
                                </div>
                            )}
                            {selectedSms?.rf1_name && (
                                <div>
                                    <p className="text-white/40 text-sm uppercase tracking-wide mb-1">Site Name</p>
                                    <p className="text-white text-sm font-mono break-all">{selectedSms.rf1_name}</p>
                                </div>
                            )}
                            {selectedSms?.rf2_id && (
                                <div>
                                    <p className="text-white/40 text-sm uppercase tracking-wide mb-1">Location Id</p>
                                    <p className="text-white text-sm font-mono break-all">{selectedSms.rf2_id}</p>
                                </div>
                            )}
                            {selectedSms?.rf2_name && (
                                <div>
                                    <p className="text-white/40 text-sm uppercase tracking-wide mb-1">Location Name</p>
                                    <p className="text-white text-sm font-mono break-all">{selectedSms.rf2_name}</p>
                                </div>
                            )}
                            <div>
                                <p className="text-white/40 text-sm uppercase tracking-wide mb-1">ID</p>
                                <p className="text-white text-sm font-mono break-all">{selectedSms._id}</p>
                            </div>
                            {selectedSms?.ref_id && (
                                <div>
                                    <p className="text-white/40 text-sm uppercase tracking-wide mb-1">Reference ID</p>
                                    <p className="text-white text-sm font-mono break-all">{selectedSms.ref_id}</p>
                                </div>
                            )}
                            <div>
                                <p className="text-white/40 text-sm uppercase tracking-wide mb-1">Status</p>
                                <div className="flex items-center space-x-2">
                                    {React.createElement(getStatusIcon(smsBodyResponse?.sms_response, selectedSms.STATUS), {
                                        className: `w-4 h-4 ${getStatusColor(smsBodyResponse?.sms_response, selectedSms.STATUS)}`
                                    })}
                                    <span className={`text-sm font-medium ${getStatusColor(smsBodyResponse?.sms_response, selectedSms.STATUS)}`}>
                                        {selectedSms.STATUS}
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <p className="text-white/40 text-sm uppercase tracking-wide mb-1">Type</p>
                                <div className="flex items-center space-x-2">
                                    {selectedSms.sms_bulk_type ? (
                                        <>
                                            <Send className="w-4 h-4 text-blue-400" />
                                            <span className="text-white">Bulk SMS</span>
                                            <span className="px-2 py-1 bg-blue-500/20 text-blue-200 text-xs rounded-full">
                                                BULK
                                            </span>
                                        </>
                                    ) : (
                                        <>
                                            <MessageSquare className="w-4 h-4 text-green-400" />
                                            <span className="text-white">Single SMS</span>
                                        </>
                                    )}
                                </div>
                            </div>
                            <div>
                                <p className="text-white/40 text-sm uppercase tracking-wide mb-1">SMS Vendor</p>
                                <p className="text-white">{selectedSms.smsVendor || 'N/A'}</p>
                            </div>
                        </div>

                        {/* SMS Response */}
                        {smsBodyResponse?.sms_response && (
                            <div>
                                <p className="text-white/40 text-sm uppercase tracking-wide mb-1">SMS Response</p>
                                <div className="bg-white/10 p-4 rounded-lg">
                                    <p className="text-white font-mono text-sm break-all">
                                        {smsBodyResponse?.sms_response || 'N/A'}
                                    </p>
                                </div>
                            </div>
                        )}

                        {/* Bulk SMS Details - PAYTM Vendor */}
                        {selectedSms.smsVendor === 'PAYTM' && smsBodyResponse?.bulkSms && smsBodyResponse?.bulkSms?.length > 0 && (
                            <div>
                                <p className="text-white/40 text-sm uppercase tracking-wide mb-3">Bulk SMS Details (PAYTM)</p>
                                <div className="space-y-4">
                                    {smsBodyResponse?.bulkSms?.map((bulk, index) => (
                                        <div key={index} className="bg-gradient-to-r from-purple-500/10 to-blue-500/10 p-4 rounded-lg border border-purple-500/20">
                                            <div className="flex items-center space-x-2 mb-4">
                                                <div className="w-8 h-8 bg-purple-500/20 rounded-full flex items-center justify-center">
                                                    <span className="text-purple-300 font-bold text-sm">P</span>
                                                </div>
                                                <span className="text-purple-300 font-semibold">PAYTM SMS #{index + 1}</span>
                                            </div>

                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                                <div>
                                                    <p className="text-white/40 text-xs uppercase tracking-wide mb-1">Location ID</p>
                                                    <div className="flex items-center space-x-2">
                                                        <User className="w-4 h-4 text-purple-400" />
                                                        <p className="text-white font-mono">{bulk?.smsData?.rf2_id || bulk?.rf2_id || 'N/A'}</p>
                                                    </div>
                                                </div>
                                                <div>
                                                    <p className="text-white/40 text-xs uppercase tracking-wide mb-1">Location Name</p>
                                                    <div className="flex items-center space-x-2">
                                                        <User className="w-4 h-4 text-purple-400" />
                                                        <p className="text-white font-mono">{bulk?.smsData?.rf2_name || bulk?.rf2_name || 'N/A'}</p>
                                                    </div>
                                                </div>
                                                <div>
                                                    <p className="text-white/40 text-xs uppercase tracking-wide mb-1">Sender ID</p>
                                                    <div className="flex items-center space-x-2">
                                                        <User className="w-4 h-4 text-purple-400" />
                                                        <p className="text-white font-mono">{bulk?.smsData?.sender || bulk?.sender || 'N/A'}</p>
                                                    </div>
                                                </div>
                                                <div>
                                                    <p className="text-white/40 text-xs uppercase tracking-wide mb-1">Receiver</p>
                                                    <div className="flex items-center space-x-2">
                                                        <Phone className="w-4 h-4 text-purple-400" />
                                                        <p className="text-white font-mono">{formatPhoneNumber(bulk.smsData?.receiver) || formatPhoneNumber(bulk?.mobileNo)
                                                            || formatPhoneNumber(bulk?.receiver)}</p>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                                <div>
                                                    <p className="text-white/40 text-xs uppercase tracking-wide mb-1">Message Type</p>
                                                    <div className="flex items-center space-x-2">
                                                        <MessageSquare className="w-4 h-4 text-purple-400" />
                                                        <span className="px-2 py-1 bg-purple-500/20 text-purple-200 text-xs rounded-full">
                                                            {bulk.smsData?.msg_type || bulk?.msg_type || 'TEXT'}
                                                        </span>
                                                    </div>
                                                </div>
                                                <div>
                                                    <p className="text-white/40 text-xs uppercase tracking-wide mb-1">App Name</p>
                                                    <div className="flex items-center space-x-2">
                                                        <Hash className="w-4 h-4 text-purple-400" />
                                                        <p className="text-white">{selectedSms.appName}</p>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="mb-4">
                                                <p className="text-white/40 text-xs uppercase tracking-wide mb-1">Message Content</p>
                                                <div className="bg-white/10 p-3 rounded-lg border border-purple-500/20">
                                                    <p className="text-white text-sm whitespace-pre-wrap">
                                                        {bulk.smsData?.content || bulk?.smsTemplate || bulk?.content || 'N/A'}
                                                    </p>
                                                </div>
                                            </div>

                                            {(bulk?.smsData || bulk) && (
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                    {(bulk?.smsData?.pe_id || bulk?.pe_id) && (
                                                        <div>
                                                            <p className="text-white/40 text-xs uppercase tracking-wide mb-1">PE ID</p>
                                                            <p className="text-white text-xs font-mono break-all">{bulk?.smsData?.pe_id || bulk?.pe_id}</p>
                                                        </div>
                                                    )}
                                                    {(bulk?.smsData?.template_id || bulk?.template_id) && (
                                                        <div>
                                                            <p className="text-white/40 text-xs uppercase tracking-wide mb-1">Template ID</p>
                                                            <p className="text-white text-xs font-mono break-all">{bulk?.smsData?.template_id || bulk?.template_id}</p>
                                                        </div>
                                                    )}
                                                    {(bulk?.smsData?.ref_id || bulk?.ref_id) && (
                                                        <div>
                                                            <p className="text-white/40 text-xs uppercase tracking-wide mb-1">Reference ID</p>
                                                            <p className="text-white text-xs font-mono break-all">{bulk?.smsData?.ref_id || bulk?.ref_id}</p>
                                                        </div>
                                                    )}
                                                    {(bulk?.smsData?.dlt_chain || bulk?.dlt_chain) && (
                                                        <div>
                                                            <p className="text-white/40 text-xs uppercase tracking-wide mb-1">DLT Chain</p>
                                                            <p className="text-white text-xs font-mono break-all">{bulk?.smsData?.dlt_chain || bulk?.dlt_chain}</p>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>

                                {smsBodyResponse?.bulk_sms_response && smsBodyResponse?.bulk_sms_response.length > 0 && (
                                    <div className="mt-4">
                                        <p className="text-white/40 text-sm uppercase tracking-wide mb-3">PAYTM Response</p>
                                        <div className="space-y-3">
                                            {smsBodyResponse?.bulk_sms_response.map((response, index) => {
                                                if (Array.isArray(response)) {
                                                    return response.map((item, subIndex) => {
                                                        let parsedResponse = {};
                                                        try {
                                                            parsedResponse = typeof item.Response === 'string'
                                                                ? JSON.parse(item.Response)
                                                                : item.Response;
                                                        } catch (e) {
                                                            parsedResponse = { raw: item.Response }
                                                        }

                                                        return (
                                                            <div key={`${index}-${subIndex}`} className="bg-gradient-to-r from-green-500/10 to-emerald-500/10 p-4 rounded-lg border border-green-500/20">
                                                                <div className="flex items-center justify-between mb-3">
                                                                    <div className="flex items-center space-x-2">
                                                                        <CheckCircle className="w-5 h-5 text-green-400" />
                                                                        <span className="text-green-300 font-semibold">Delivery Status</span>
                                                                    </div>
                                                                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${item.status === 'SUCCESS'
                                                                        ? 'bg-green-500/20 text-green-200'
                                                                        : 'bg-red-500/20 text-red-200'
                                                                        }`}>
                                                                        {item.status}
                                                                    </span>
                                                                </div>

                                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                                                    {parsedResponse.status && (
                                                                        <div>
                                                                            <p className="text-white/40 text-xs uppercase tracking-wide mb-1">Status Code</p>
                                                                            <p className="text-white text-sm font-mono">{parsedResponse.status}</p>
                                                                        </div>
                                                                    )}
                                                                    {parsedResponse.RESPONSE && (
                                                                        <div>
                                                                            <p className="text-white/40 text-xs uppercase tracking-wide mb-1">Response</p>
                                                                            <p className="text-white text-sm font-mono">{parsedResponse.RESPONSE}</p>
                                                                        </div>
                                                                    )}
                                                                    {parsedResponse.DESCRIPTION && (
                                                                        <div className="md:col-span-2">
                                                                            <p className="text-white/40 text-xs uppercase tracking-wide mb-1">Description</p>
                                                                            <p className="text-white text-sm">{parsedResponse.DESCRIPTION}</p>
                                                                        </div>
                                                                    )}
                                                                    {parsedResponse.MESSAGE_ID && (
                                                                        <div className="md:col-span-2">
                                                                            <p className="text-white/40 text-xs uppercase tracking-wide mb-1">Message ID</p>
                                                                            <p className="text-white text-xs font-mono break-all">{parsedResponse.MESSAGE_ID}</p>
                                                                        </div>
                                                                    )}
                                                                    {parsedResponse.TIME && (
                                                                        <div>
                                                                            <p className="text-white/40 text-xs uppercase tracking-wide mb-1">Time</p>
                                                                            <div className="flex items-center space-x-2">
                                                                                <Clock className="w-4 h-4 text-green-400" />
                                                                                <p className="text-white text-sm">{parsedResponse.TIME}</p>
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                                    {parsedResponse?.raw && (
                                                                        <div>
                                                                            <p className="text-white/40 text-xs uppercase tracking-wide mb-1">Raw Response</p>
                                                                            <div className="flex items-center space-x-2">
                                                                                <Clock className="w-4 h-4 text-green-400" />
                                                                                <p className="text-white text-sm">{parsedResponse?.raw}</p>
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        );
                                                    });
                                                } else if (response && typeof response === 'object') {
                                                    return (
                                                        <div key={index} className="bg-white/10 p-4 rounded-lg border border-white/10">
                                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                                                {response.mobile_no && (
                                                                    <div>
                                                                        <p className="text-white/40 text-xs uppercase tracking-wide mb-1">Mobile Number</p>
                                                                        <div className="flex items-center space-x-2">
                                                                            <Phone className="w-4 h-4 text-white/60" />
                                                                            <p className="text-white font-mono">{formatPhoneNumber(response.mobile_no)}</p>
                                                                        </div>
                                                                    </div>
                                                                )}
                                                                {response.ref_id && (
                                                                    <div>
                                                                        <p className="text-white/40 text-xs uppercase tracking-wide mb-1">Reference ID</p>
                                                                        <p className="text-white text-xs font-mono break-all">{response.ref_id}</p>
                                                                    </div>
                                                                )}
                                                                {response.STATUS && (
                                                                    <div>
                                                                        <p className="text-white/40 text-xs uppercase tracking-wide mb-1">Status</p>
                                                                        <span className={`px-2 py-1 rounded-full text-xs ${response.STATUS === 'SUCCESS'
                                                                            ? 'bg-green-500/20 text-green-200'
                                                                            : 'bg-red-500/20 text-red-200'
                                                                            }`}>
                                                                            {response.STATUS}
                                                                        </span>
                                                                    </div>
                                                                )}
                                                                {response.time && (
                                                                    <div>
                                                                        <p className="text-white/40 text-xs uppercase tracking-wide mb-1">Time</p>
                                                                        <div className="flex items-center space-x-2">
                                                                            <Clock className="w-4 h-4 text-white/60" />
                                                                            <p className="text-white text-sm">{response.time}</p>
                                                                        </div>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    );
                                                }
                                                return null;
                                            })}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Bulk SMS Details - Other Vendors */}
                        {selectedSms.smsVendor !== 'PAYTM' && selectedSms.sms_bulk_type && (
                            <div>
                                <p className="text-white/40 text-sm uppercase tracking-wide mb-3">Bulk SMS Details</p>
                                <div className="space-y-4">

                                    {/* CASE 1: bulkSms exists */}
                                    {smsBodyResponse?.bulkSms && smsBodyResponse?.bulkSms?.length > 0 && (
                                        <>
                                            {smsBodyResponse?.bulkSms?.map((bulk, index) => (
                                                <div key={index} className="bg-white/5 p-4 rounded-lg border border-white/10">
                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                                        <div>
                                                            <p className="text-white/40 text-xs uppercase tracking-wide mb-1">Location ID</p>
                                                            <div className="flex items-center space-x-2">
                                                                <User className="w-4 h-4 text-purple-400" />
                                                                <p className="text-white font-mono">{bulk?.smsData?.rf2_id || bulk?.rf2_id || 'N/A'}</p>
                                                            </div>
                                                        </div>
                                                        <div>
                                                            <p className="text-white/40 text-xs uppercase tracking-wide mb-1">Location Name</p>
                                                            <div className="flex items-center space-x-2">
                                                                <User className="w-4 h-4 text-purple-400" />
                                                                <p className="text-white font-mono">{bulk?.smsData?.rf1_name || bulk?.rf2_name || 'N/A'}</p>
                                                            </div>
                                                        </div>
                                                        <div>
                                                            <p className="text-white/40 text-xs uppercase tracking-wide mb-1">Mobile Number</p>
                                                            <div className="flex items-center space-x-2">
                                                                <Phone className="w-4 h-4 text-white/60" />
                                                                <p className="text-white font-mono">{formatPhoneNumber(bulk.mobileNo)}</p>
                                                            </div>
                                                        </div>
                                                        <div>
                                                            <p className="text-white/40 text-xs uppercase tracking-wide mb-1">App Name</p>
                                                            <div className="flex items-center space-x-2">
                                                                <User className="w-4 h-4 text-white/60" />
                                                                <p className="text-white">{bulk?.appName || selectedSms.appName}</p>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div className="mb-4">
                                                        <p className="text-white/40 text-xs uppercase tracking-wide mb-1">SMS Template</p>
                                                        <div className="bg-white/10 p-3 rounded-lg">
                                                            <p className="text-white text-sm whitespace-pre-wrap">{bulk.smsTemplate}</p>
                                                        </div>
                                                    </div>

                                                    {bulk.smsData && Object.keys(bulk.smsData).length > 0 && (
                                                        <div>
                                                            <p className="text-white/40 text-xs uppercase tracking-wide mb-2">SMS Data</p>
                                                            <div className="bg-white/10 p-3 rounded-lg">
                                                                <pre className="text-white text-sm overflow-x-auto">
                                                                    {JSON.stringify(bulk.smsData, null, 2)}
                                                                </pre>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </>
                                    )}

                                    {/* CASE 2: bulkSms is null but bulk_template_sms exists */}
                                    {!selectedSms?.bulkSms && smsBodyResponse?.bulk_template_sms && (
                                        <div className="bg-white/5 p-4 rounded-lg border border-white/10">
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                                <div>
                                                    <p className="text-white/40 text-xs uppercase tracking-wide mb-1">Mobile Number</p>
                                                    <div className="flex items-center space-x-2">
                                                        <Phone className="w-4 h-4 text-white/60" />
                                                        <p className="text-white font-mono">
                                                            {extractMobileNumbers(smsBodyResponse?.bulk_template_sms).join(", ")}
                                                        </p>
                                                    </div>
                                                </div>

                                                <div>
                                                    <p className="text-white/40 text-xs uppercase tracking-wide mb-1">App Name</p>
                                                    <div className="flex items-center space-x-2">
                                                        <User className="w-4 h-4 text-white/60" />
                                                        <p className="text-white">{selectedSms.appName}</p>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="mb-4">
                                                <p className="text-white/40 text-xs uppercase tracking-wide mb-1">SMS Template</p>
                                                <div className="bg-white/10 p-3 rounded-lg">
                                                    <p className="text-white text-sm whitespace-pre-wrap">
                                                        {smsBodyResponse?.bulk_template_sms}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                </div>

                                {/* Bulk SMS Response (unchanged) */}
                                {smsBodyResponse?.bulk_sms_response && smsBodyResponse?.bulk_sms_response.length > 0 && (
                                    <div className="mt-4">
                                        <p className="text-white/40 text-sm uppercase tracking-wide mb-2">Bulk SMS Response</p>
                                        <div className="bg-white/10 p-4 rounded-lg">
                                            <div className="flex flex-wrap gap-2">
                                                {normalizeBulkSmsResponse(smsBodyResponse?.bulk_sms_response).map((resp, i) => {
                                                    const key = resp?.MID ?? resp?.mobile_no ?? i;

                                                    return (
                                                        <div
                                                            key={key}
                                                            className="px-3 py-2 bg-green-500/20 text-green-200 rounded-lg text-sm font-mono flex flex-col gap-1"
                                                        >
                                                            {Object.entries(resp).map(([k, v]) => (
                                                                <span key={k} className="block">
                                                                    <strong className="text-white/60">{k}:</strong> {String(v)}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    );
                                                })}

                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Single SMS Details */}
                        {!selectedSms.sms_bulk_type && smsBodyResponse?.sms && (
                            <div>
                                <p className="text-white/40 text-sm uppercase tracking-wide mb-3">SMS Details</p>
                                <div className="bg-white/5 p-4 rounded-lg border border-white/10">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                        <div>
                                            <p className="text-white/40 text-xs uppercase tracking-wide mb-1">Location ID</p>
                                            <div className="flex items-center space-x-2">
                                                <User className="w-4 h-4 text-purple-400" />
                                                <p className="text-white font-mono">{smsBodyResponse?.sms?.rf2_id || 'N/A'}</p>
                                            </div>
                                        </div>
                                        <div>
                                            <p className="text-white/40 text-xs uppercase tracking-wide mb-1">Location Name</p>
                                            <div className="flex items-center space-x-2">
                                                <User className="w-4 h-4 text-purple-400" />
                                                <p className="text-white font-mono">{smsBodyResponse?.sms?.rf2_name || 'N/A'}</p>
                                            </div>
                                        </div>
                                        <div>
                                            <p className="text-white/40 text-xs uppercase tracking-wide mb-1">Mobile Number</p>
                                            <div className="flex items-center space-x-2">
                                                <Phone className="w-4 h-4 text-white/60" />
                                                <p className="text-white font-mono">{formatPhoneNumber(smsBodyResponse?.sms?.mobileNo)}</p>
                                            </div>
                                        </div>
                                        <div>
                                            <p className="text-white/40 text-xs uppercase tracking-wide mb-1">App Name</p>
                                            <div className="flex items-center space-x-2">
                                                <User className="w-4 h-4 text-white/60" />
                                                <p className="text-white">{smsBodyResponse?.sms?.appName}</p>
                                            </div>
                                        </div>
                                    </div>

                                    <div>
                                        <p className="text-white/40 text-xs uppercase tracking-wide mb-2">SMS Template</p>
                                        <div className="bg-white/10 p-3 rounded-lg">
                                            <p className="text-white text-sm whitespace-pre-wrap">
                                                {smsBodyResponse?.sms?.smsTemplate}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Timestamp Info */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                                <p className="text-white/40 text-sm uppercase tracking-wide mb-1">Date</p>
                                <div className="flex items-center space-x-2">
                                    <Calendar className="w-4 h-4 text-white/60" />
                                    <p className="text-white">{selectedSms.date}</p>
                                </div>
                            </div>
                            <div>
                                <p className="text-white/40 text-sm uppercase tracking-wide mb-1">Creation Time</p>
                                <div className="flex items-center space-x-2">
                                    <Clock className="w-4 h-4 text-white/60" />
                                    <p className="text-white">{selectedSms.creation_time_str}</p>
                                </div>
                            </div>
                            <div>
                                <p className="text-white/40 text-sm uppercase tracking-wide mb-1">Timestamp (MS)</p>
                                <p className="text-white font-mono text-sm">{selectedSms.creation_time_ms}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Error Payload Modal */}
            {showErrorPayload && smsBodyResponse?.payload_error_list && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="bg-slate-900 border border-red-500/30 rounded-2xl p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="flex items-center justify-between mb-6">
                            <div className="flex items-center gap-3">
                                <AlertCircle className="w-6 h-6 text-red-400" />
                                <h3 className="text-2xl font-bold text-white">Error Payload Details</h3>
                            </div>
                            <button
                                onClick={() => setShowErrorPayload(false)}
                                className="w-8 h-8 bg-white/10 rounded-full flex items-center justify-center hover:bg-white/20 transition-all duration-200"
                            >
                                <XCircle className="w-5 h-5 text-white" />
                            </button>
                        </div>

                        <div className="space-y-6">
                            {smsBodyResponse.payload_error_list.map((errorItem, index) => (
                                <div key={index} className="bg-gradient-to-r from-red-500/10 to-orange-500/10 p-4 rounded-lg border border-red-500/20">
                                    <div className="flex items-center space-x-2 mb-4">
                                        <div className="w-8 h-8 bg-red-500/20 rounded-full flex items-center justify-center">
                                            <span className="text-red-300 font-bold text-sm">{index + 1}</span>
                                        </div>
                                        <span className="text-red-300 font-semibold">Error #{index + 1}</span>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                        {errorItem?.ref_id && (
                                            <div>
                                                <p className="text-white/40 text-xs uppercase tracking-wide mb-1">Reference ID</p>
                                                <p className="text-white text-sm font-mono break-all">{errorItem.ref_id}</p>
                                            </div>
                                        )}
                                        {errorItem?.error_reason && (
                                            <div>
                                                <p className="text-white/40 text-xs uppercase tracking-wide mb-1">Error Reason</p>
                                                <p className="text-red-200 text-sm font-semibold">{errorItem.error_reason}</p>
                                            </div>
                                        )}
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                        {errorItem?.receiver && (
                                            <div>
                                                <p className="text-white/40 text-xs uppercase tracking-wide mb-1">Receiver</p>
                                                <div className="flex items-center space-x-2">
                                                    <Phone className="w-4 h-4 text-red-400" />
                                                    <p className="text-white font-mono">{formatPhoneNumber(errorItem.receiver) || 'N/A'}</p>
                                                </div>
                                            </div>
                                        )}
                                        {errorItem?.sender && (
                                            <div>
                                                <p className="text-white/40 text-xs uppercase tracking-wide mb-1">Sender</p>
                                                <div className="flex items-center space-x-2">
                                                    <User className="w-4 h-4 text-red-400" />
                                                    <p className="text-white">{errorItem.sender}</p>
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                        {errorItem?.msz_type && (
                                            <div>
                                                <p className="text-white/40 text-xs uppercase tracking-wide mb-1">Message Type</p>
                                                <div className="flex items-center space-x-2">
                                                    <MessageSquare className="w-4 h-4 text-red-400" />
                                                    <span className="px-2 py-1 bg-red-500/20 text-red-200 text-xs rounded-full">
                                                        {errorItem.msz_type}
                                                    </span>
                                                </div>
                                            </div>
                                        )}
                                        {errorItem?.template_id && (
                                            <div>
                                                <p className="text-white/40 text-xs uppercase tracking-wide mb-1">Template ID</p>
                                                <p className="text-white text-sm font-mono break-all">{errorItem.template_id}</p>
                                            </div>
                                        )}
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                        {errorItem?.pe_id && (
                                            <div>
                                                <p className="text-white/40 text-xs uppercase tracking-wide mb-1">PE ID</p>
                                                <p className="text-white text-sm font-mono break-all">{errorItem.pe_id}</p>
                                            </div>
                                        )}
                                        {errorItem?.dlt_chain && (
                                            <div>
                                                <p className="text-white/40 text-xs uppercase tracking-wide mb-1">DLT Chain</p>
                                                <p className="text-white text-sm font-mono break-all">{errorItem.dlt_chain}</p>
                                            </div>
                                        )}
                                    </div>

                                    {errorItem?.content && (
                                        <div>
                                            <p className="text-white/40 text-xs uppercase tracking-wide mb-1">Message Content</p>
                                            <div className="bg-white/10 p-3 rounded-lg border border-red-500/20">
                                                <p className="text-white text-sm whitespace-pre-wrap">
                                                    {errorItem.content}
                                                </p>
                                            </div>
                                        </div>
                                    )}

                                    {errorItem?.tag_name && (
                                        <div className="mt-4">
                                            <p className="text-white/40 text-xs uppercase tracking-wide mb-1">Tag Name</p>
                                            <p className="text-white text-sm">{errorItem.tag_name || 'N/A'}</p>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}